import React from 'react';
import {
    Alert,
    Button,
    Col,
    Empty,
    List,
    Modal,
    Progress,
    Row,
    Spin,
    Statistic,
    Typography
} from 'antd';
import {
    CheckOutlined,
    FileWordOutlined
} from '@ant-design/icons';
import ProductKnowledgeDocxPreview from './ProductKnowledgeDocxPreview';

const { Paragraph, Text } = Typography;

export default function ProductKnowledgeReviewModal(props) {
    const {
        visible,
        loading,
        committing,
        downloading,
        draft,
        progress,
        document,
        documentLoading,
        documentError,
        onCancel,
        onCommit,
        onOpenDocument
    } = props;

    const isProcessing = loading || committing;
    const progressEvents = (progress?.events || []).slice().reverse();
    const hasProgressTotal = Number(progress?.total) > 0;
    const progressPercent = hasProgressTotal
        ? Math.min(100, Math.round((Number(progress?.current || 0) / Number(progress.total)) * 100))
        : 0;
    const renderProgress = () => (
        <div style={{
            background: '#f6fbff',
            border: '1px solid #d6e9f8',
            borderRadius: 4,
            padding: 16,
            marginBottom: loading ? 0 : 16
        }}>
            {loading && <Spin size="large" style={{ marginBottom: 16 }} />}
            <div>
                <Text strong>{progress?.message || 'Đang xử lý tri thức sản phẩm…'}</Text>
                <br />
                <Text type="secondary">
                    {hasProgressTotal
                        ? `Tiến độ ${progress?.current || 0}/${progress.total}`
                        : 'Kết nối tiến độ đang hoạt động'}
                </Text>
            </div>
            {hasProgressTotal && (
                <Progress
                    percent={progressPercent}
                    status="active"
                    style={{ marginTop: 12, marginBottom: progressEvents.length ? 8 : 0 }}
                />
            )}
            {progressEvents.length > 0 && (
                <List
                    size="small"
                    style={{
                        marginTop: 8,
                        maxHeight: 156,
                        overflowY: 'auto',
                        background: '#fff',
                        border: '1px solid #e8eef3'
                    }}
                    dataSource={progressEvents}
                    renderItem={(item) => (
                        <List.Item style={{ padding: '6px 10px' }}>
                            <Text type="secondary">{item.message}</Text>
                        </List.Item>
                    )}
                />
            )}
        </div>
    );

    const footer = draft ? [
        <Button key="cancel" onClick={onCancel} disabled={committing}>
            Hủy
        </Button>,
        <Button
            key="docx"
            icon={<FileWordOutlined />}
            loading={downloading}
            onClick={onOpenDocument}
            disabled={committing}
        >
            Tải bản DOCX gốc
        </Button>,
        <Button
            key="commit"
            type="primary"
            icon={<CheckOutlined />}
            loading={committing}
            onClick={onCommit}
        >
            Đồng ý nạp tri thức
        </Button>
    ] : [
        <Button key="close" onClick={onCancel} disabled={loading}>
            Đóng
        </Button>
    ];

    return (
        <Modal
            title={(
                <span>
                    <FileWordOutlined style={{ color: '#2E74B5', marginRight: 8 }} />
                    Review bộ tri thức sản phẩm
                </span>
            )}
            visible={visible}
            width={1100}
            closable={!loading && !committing}
            maskClosable={false}
            onCancel={onCancel}
            footer={footer}
            destroyOnClose
        >
            {loading ? (
                <div style={{ textAlign: 'center', padding: '32px 16px' }}>
                    {renderProgress()}
                    <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
                        Thời gian xử lý phụ thuộc số lượng ảnh trong catalog.
                    </Text>
                </div>
            ) : !draft ? (
                <Empty description="Chưa có bản review" />
            ) : (
                <>
                    {isProcessing && renderProgress()}
                    <div style={{
                        background: '#fafafa',
                        border: '1px solid #f0f0f0',
                        padding: 16,
                        marginBottom: 16
                    }}>
                        <Text strong>{draft.fileName}</Text>
                        <br />
                        <Text type="secondary">
                            Tạo lúc {new Date(draft.createdAt).toLocaleString('vi-VN')}. Hãy kiểm tra nội dung trước khi xác nhận.
                        </Text>
                    </div>

                    <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                        <Col xs={24} sm={8}>
                            <div style={{ border: '1px solid #f0f0f0', padding: 12 }}>
                                <Statistic title="Sản phẩm" value={draft.stats?.products || 0} />
                            </div>
                        </Col>
                        <Col xs={24} sm={8}>
                            <div style={{ border: '1px solid #f0f0f0', padding: 12 }}>
                                <Statistic title="Biến thể" value={draft.stats?.variants || 0} />
                            </div>
                        </Col>
                        <Col xs={24} sm={8}>
                            <div style={{ border: '1px solid #f0f0f0', padding: 12 }}>
                                <Statistic title="Hình ảnh đã quét" value={draft.stats?.images || 0} />
                            </div>
                        </Col>
                    </Row>

                    {(draft.warnings || []).length > 0 && (
                        <Alert
                            style={{ marginBottom: 16 }}
                            type="warning"
                            showIcon
                            message={`${draft.warnings.length} ảnh cần kiểm tra thủ công`}
                            description={(draft.warnings || []).slice(0, 3).join('\n')}
                        />
                    )}

                    <Text strong>Preview tài liệu DOCX</Text>
                    <div style={{ marginTop: 8 }}>
                        <ProductKnowledgeDocxPreview
                            document={document}
                            loading={documentLoading}
                            error={documentError}
                        />
                    </div>
                </>
            )}
        </Modal>
    );
}
